import React, { useEffect, useState } from 'react';
import { ArrowLeft, BookOpen, Sparkles } from 'lucide-react';
import { NewsItem, Language } from '../../types';
import { t } from '../../i18n';
import * as footballService from '../../services/footballService';
import SEO from '../Common/SEO';
import { WebtoonLogo } from '../Common/WebtoonLogo';

interface WebtoonListViewProps {
  onBack: () => void;
  lang: Language;
}

export const WebtoonListView: React.FC<WebtoonListViewProps> = ({ onBack, lang }) => {
  const [webtoons, setWebtoons] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWebtoons = async () => {
      try {
        const result = await footballService.fetchSupabaseNewsList(lang, 0, 24, undefined, 'webtoon');
        setWebtoons(result.news);
      } catch (error) {
        console.error('Failed to fetch webtoons:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWebtoons();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [lang]);

  return (
    <div className="flex flex-col min-h-screen bg-[#f8f9fa] text-[#2c2c2c] font-sans">
      <SEO 
        title={t(lang, 'webtoonSeoTitle')} 
        description={t(lang, 'webtoonSeoDesc')}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
          >
            <ArrowLeft size={22} strokeWidth={2.5} />
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black tracking-tight text-slate-900">WEBTOON</h1>
          </div>
        </div>
      </header>

      {/* Featured Banner / Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 border-b border-slate-800">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/webtoon/banner.png" 
            alt="Webtoon Banner" 
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        </div>

        <div className="relative z-10 px-6 py-12 max-w-7xl mx-auto flex flex-col items-start text-left">
          
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[1.1] text-white mb-6 max-w-2xl">
            {t(lang, 'webtoonHeroTitle')}<br/>
            <span className="text-[#00d564]">{t(lang, 'webtoonHeroTitleColor')}</span>
          </h2>
          
          <p className="text-base md:text-lg text-slate-300 max-w-md font-medium leading-relaxed mb-4">
            {t(lang, 'webtoonHeroDesc')}
          </p>
        </div>

        {/* Floating Accent Shapes */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#00d564]/10 rounded-full -mr-20 -mt-20 blur-3xl animate-pulse"></div>
      </section>

      {/* Quick Navigation / Categories (Optional Style) */}
      {/* <nav className="sticky top-[61px] z-40 bg-white border-b border-slate-200 px-4 flex items-center gap-6 overflow-x-auto scrollbar-hide">
        {['인기', '신작', '완결', '요일별'].map((cat, idx) => (
          <button 
            key={cat} 
            className={`py-3 px-1 text-sm font-bold whitespace-nowrap transition-all border-b-2 ${idx === 0 ? 'border-[#00d564] text-[#00d564]' : 'border-transparent text-slate-400'}`}
          >
            {cat}
          </button>
        ))}
      </nav> */}

      {/* Webtoon Grid */}
      <main className="flex-1 px-4 py-6 pb-40">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-8 md:gap-x-6 md:gap-y-10">
          {loading ? (
            Array(12).fill(0).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4.2] bg-slate-200 rounded-lg mb-3"></div>
                <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                <div className="h-2 bg-slate-200 rounded w-2/3"></div>
              </div>
            ))
          ) : (
            webtoons.map((item) => (
              <div 
                key={item.id}
                onClick={() => window.navigateTo(`/feed/item/${item.id}`)}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4.2] rounded-lg overflow-hidden bg-slate-100 shadow-sm mb-2.5">
                  <img src={item.image} alt="" className="w-full h-full object-cover" />
                  
                  {/* Badge */}
                  <div className="absolute top-2 left-2">
                    <span className="bg-[#00d564] text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm">UP</span>
                  </div>
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                    <span className="text-white text-[10px] font-bold flex items-center gap-1">
                      <BookOpen size={10} /> {item.time}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-0.5">
                  <h3 className="text-[12px] md:text-sm font-bold text-slate-900 line-clamp-2 leading-snug group-hover:text-[#00d564] transition-colors">
                    {item.title}
                  </h3>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && webtoons.length === 0 && (
          <div className="py-20 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="text-slate-300" size={32} />
            </div>
            <p className="text-slate-400 font-medium">{t(lang, 'noWebtoons')}</p>
          </div>
        )}
      </main>
    </div>
  );
};
